import React from 'react';
import { Bot, Sparkles, Info, AlertTriangle, Clock } from 'lucide-react';

// Beta mode label - AI is now live
const AI_BETA_LABEL = "AI Assistant — Live (Beta)";

export function AIButton({ 
  children, 
  icon: Icon = Sparkles,
  tooltip = "AI generation is live",
  variant = 'default',
  className = '',
  onClick,
  disabled = false
}) {
  return (
    <div className="ai-button-wrapper" title={tooltip}>
      <button 
        className={`ai-button ${variant} ${className}`}
        onClick={onClick}
        disabled={disabled}
      >
        <Icon size={14} />
        <span>{children}</span>
      </button>
    </div>
  );
}

export function AIPanel({ 
  title = AI_BETA_LABEL,
  description,
  children,
  className = ''
}) {
  return (
    <div className={`ai-panel ${className}`}>
      <div className="ai-panel-header">
        <Bot size={18} className="ai-panel-icon" />
        <h3 className="ai-panel-title">{title}</h3>
        <span className="ai-beta-badge">BETA</span>
      </div>
      {description && (
        <p className="ai-panel-description">{description}</p>
      )}
      <div className="ai-panel-content">
        {children}
      </div>
    </div>
  );
}

export function AIChip({ 
  children, 
  onClick,
  disabled = false 
}) {
  return (
    <button 
      className="ai-chip"
      onClick={onClick}
      disabled={disabled}
      title="AI insights available"
    >
      <Sparkles size={12} />
      {children}
    </button>
  );
}

export function AIExplanationBox({ children, className = '', dataSources = null }) {
  return (
    <div className={`ai-explanation-box ${className}`}>
      <Bot size={16} className="ai-explanation-icon" />
      <div style={{ flex: 1 }}>
        <p style={{ marginBottom: dataSources ? 'var(--space-sm)' : 0 }}>{children}</p>
        {dataSources && <AIDataSourcesInline dataSources={dataSources} />}
      </div>
    </div>
  );
}

export function AIBadge({ text = "AI Live (Beta)" }) {
  return (
    <span className="ai-badge ai-badge-live">
      <Sparkles size={10} />
      {text}
    </span>
  );
}

export function AIBetaLabel() {
  return (
    <span className="ai-beta-label">
      <Bot size={12} />
      {AI_BETA_LABEL}
    </span>
  );
}

/**
 * Inline data sources display for AI responses
 */
export function AIDataSourcesInline({ dataSources }) {
  if (!dataSources) return null;
  
  const { data_sources, stale_warnings, has_stale_data } = dataSources;
  
  if (!data_sources || data_sources.length === 0) return null;
  
  return (
    <div 
      data-testid="ai-data-sources-inline"
      style={{
        paddingTop: 'var(--space-sm)',
        borderTop: '1px solid var(--glass-border)',
        fontSize: '0.6875rem',
        color: 'var(--text-muted)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        <Info size={10} />
        <span>Based on:</span>
        {data_sources.map((source, idx) => (
          <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
            <Clock size={8} />
            {source.source} ({source.last_sync})
            {idx < data_sources.length - 1 && ','}
          </span>
        ))}
      </div>
      {has_stale_data && stale_warnings?.length > 0 && (
        <div style={{ 
          marginTop: '4px', 
          color: 'var(--status-warning)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <AlertTriangle size={10} />
          <span>{stale_warnings[0]}</span>
        </div>
      )}
    </div>
  );
}

/**
 * AI Response wrapper that includes data source transparency
 */
export function AIResponseBox({ content, dataSources, className = '' }) {
  return (
    <div 
      className={`ai-response-box ${className}`}
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
        <Bot size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ flex: 1 }}>
          <div style={{ color: 'var(--text-primary)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            {content}
          </div>
          {dataSources && (
            <div style={{ marginTop: 'var(--space-md)' }}>
              <AIDataSourcesInline dataSources={dataSources} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default { AIButton, AIPanel, AIChip, AIExplanationBox, AIBadge, AIBetaLabel, AIDataSourcesInline, AIResponseBox };
